import { HTMLAttributes } from 'react';
import {
  HeaderGroup,
  flexRender,
  Table,
  FilterType,
} from '@tanstack/react-table';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';
import { TableHeader, TableHead, TableRow, cn } from '@sib/ui';
import {
  getTableColumnPinningProperties,
  getTableHeaderFixedSizingStyle,
  getTableHeaderWidth,
} from '../../utils';
import { DataTableHeaderResizeHandler } from './DataTableHeaderResizeHandler';
import {
  useGetTableColumnOptions,
  useResizeConstraints,
} from './DataTableHeader.utils';

interface DataTableHeaderProps<T>
  extends HTMLAttributes<HTMLTableSectionElement> {
  table: Table<T>;
  headerGroups: HeaderGroup<T>[];
  hideRounding?: boolean;
  withFilters?: boolean;
  withFixedColumnWidth?: boolean;
}

export const DataTableHeader = <T,>({
  table,
  headerGroups,
  hideRounding = false,
  withFilters,
  withFixedColumnWidth = false,
}: DataTableHeaderProps<T>) => {
  const getTableColumnOptions = useGetTableColumnOptions();
  const { resizeJustEndedRef } = useResizeConstraints<T>({ table });

  return (
    <TableHeader className="relative">
      {headerGroups.map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            const filter = header.column.columnDef.filter;
            const sorting = header.column.getIsSorted();
            const canUserSort = header.column.columnDef.sortable ?? true;
            const meta = header.column.columnDef.meta;
            const options = getTableColumnOptions<T>(header.column);
            const rawFilterValue = header.column.getFilterValue();
            const arrayVal =
              rawFilterValue === EMPTY_FILTER_VALUE
                ? [EMPTY_FILTER_VALUE]
                : Array.isArray(rawFilterValue)
                  ? rawFilterValue
                  : rawFilterValue !== undefined && rawFilterValue !== null
                    ? [String(rawFilterValue)]
                    : [];
            const filterProp = filter && {
              type: filter?.FilterProps?.type as FilterType,
              value: arrayVal as string[],
              onChange: (vals: string[]) => {
                if (vals.length === 0)
                  return header.column.setFilterValue(undefined);
                if (vals.length === 1 && vals[0] === EMPTY_FILTER_VALUE)
                  return header.column.setFilterValue(EMPTY_FILTER_VALUE);
                header.column.setFilterValue(vals);
              },
              disabled: filter?.disabled,
            };
            const size = header.column.getSize();
            const className = meta?.className;
            const aplicableClassName =
              typeof className === 'string'
                ? className
                : className?.(null as T);

            const { pinPosition, style } = getTableColumnPinningProperties(
              header.column
            );

            const widthStyle = withFixedColumnWidth
              ? getTableHeaderFixedSizingStyle(header.id)
              : {
                  minWidth: 0,
                  width: getTableHeaderWidth(header.id),
                };

            return (
              <TableHead
                key={header.id}
                canSort={canUserSort}
                size={size}
                style={{
                  ...style,
                  ...widthStyle,
                }}
                tooltip={meta?.headerTooltip}
                onClick={() => {
                  if (resizeJustEndedRef.current)
                    return (resizeJustEndedRef.current = false);

                  if (!canUserSort) return;
                  if (sorting && sorting === 'desc')
                    return header.column.clearSorting();
                  if (sorting && sorting === 'asc')
                    return header.column.toggleSorting(true);
                  header.column.toggleSorting(false);
                }}
                filter={filterProp}
                clearFilter={() => header.column.setFilterValue(undefined)}
                setFilter={(vals: string[]) =>
                  header.column.setFilterValue(vals)
                }
                filterProps={filter?.FilterProps}
                withFilters={withFilters}
                options={options}
                className={cn(
                  aplicableClassName,
                  'sticky top-0 z-20 overflow-visible whitespace-nowrap',
                  hideRounding && 'top-10',
                  pinPosition && 'sticky z-30',
                  header.index === 0 && 'bg-primary! text-white',
                  hideRounding &&
                    (header.index === 0 ||
                      header.index === headerGroup.headers.length - 1) &&
                    'rounded-tl-none! rounded-tr-none!',
                  header.index === headerGroup.headers.length - 1 &&
                    'bg-primary! text-white'
                )}
                {...(sorting && { sortDirection: sorting })}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
                {header.column.getCanResize() && (
                  <DataTableHeaderResizeHandler table={table} header={header} />
                )}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
};
